"use client"

import { ModelShowcaseItemDto } from "api"
import { useRef } from "react"
import SwiperCore, { A11y, Keyboard } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import { trackModelsCarouselClick } from "utils/gtm"
import {
  SlideButton,
  SlideImage,
  SlideLink,
  SwiperWrap,
} from "./modelShowcase.styled"

type Props = {
  models: ModelShowcaseItemDto[]
  onActiveIndexChange: (index: number) => void
  onSwiper: (swiper: SwiperCore) => void
}

const ModelImageSwiper = ({ models, onActiveIndexChange, onSwiper }: Props) => {
  const enableLoop = models.length > 2
  // Keep a local ref to call slideToLoop() from slide buttons.
  const swiperRef = useRef<SwiperCore | null>(null)

  const handleSwiper = (swiper: SwiperCore) => {
    swiperRef.current = swiper
    onSwiper(swiper)
  }

  return (
    <SwiperWrap>
      <Swiper
        modules={[A11y, Keyboard]}
        dir="rtl"
        centeredSlides
        loop={enableLoop}
        slidesPerView="auto"
        slideToClickedSlide
        keyboard={{ enabled: true }}
        a11y={{ enabled: true }}
        onSwiper={handleSwiper}
        onSlideChange={(swiper) => onActiveIndexChange(swiper.realIndex)}
      >
        {models.map((model, index) => {
          const image = (
            <SlideImage
              src={model.modelImage?.imageUrl}
              alt={model.modelImage?.alt ?? model.modelName}
              loading="lazy"
            />
          )
          return (
            <SwiperSlide key={model.id}>
              {/* The centered car links to the model page (same destination as the CTA);
                  side cars stay buttons that bring the car to the center. Swiper's default
                  preventClicks swallows the click after a swipe, so dragging never navigates. */}
              {({ isActive }) =>
                isActive && model.ctaUrl ? (
                  <SlideLink
                    href={model.ctaUrl}
                    aria-label={model.modelName}
                    onClick={() =>
                      trackModelsCarouselClick(model.modelName ?? "")
                    }
                  >
                    {image}
                  </SlideLink>
                ) : (
                  <SlideButton
                    type="button"
                    aria-label={model.modelName}
                    onClick={() => swiperRef.current?.slideToLoop(index)}
                  >
                    {image}
                  </SlideButton>
                )
              }
            </SwiperSlide>
          )
        })}
      </Swiper>
    </SwiperWrap>
  )
}

export default ModelImageSwiper
